import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface Country {
  code: string;
  name: string;
  flag: string;
  name_fr: string;
}

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private countries: Country[] = [];
  private countriesLoaded = false;

  constructor(private http: HttpClient) {}

  /**
   * Récupère tous les pays
   * @param language - Langue pour le nom du pays ('en' ou 'fr')
   * @returns Observable<Country[]>
   */
  getCountries(language: 'en' | 'fr' = 'fr'): Observable<Country[]> {
    if (this.countriesLoaded) {
      return of(this.countries);
    }

    return this.http.get<Country[]>('/data/countries.json').pipe(
      map((countries) => {
        this.countries = countries;
        this.countriesLoaded = true;
        return countries;
      }),
      catchError((error) => {
        console.error('Erreur lors du chargement des pays:', error);
        return of([]);
      })
    );
  }

  /**
   * Récupère un pays par son code ISO
   * @param code - Code ISO du pays
   * @returns Observable<Country | null>
   */
  getCountryByCode(code: string): Observable<Country | null> {
    return this.getCountries().pipe(
      map((countries) => {
        return (
          countries.find((country) => country.code === code.toUpperCase()) ||
          null
        );
      })
    );
  }

  /**
   * Recherche des pays par nom
   * @param searchTerm - Terme de recherche
   * @param language - Langue pour la recherche ('en' ou 'fr')
   * @returns Observable<Country[]>
   */
  searchCountries(
    searchTerm: string,
    language: 'en' | 'fr' = 'fr'
  ): Observable<Country[]> {
    return this.getCountries().pipe(
      map((countries) => {
        const term = searchTerm.toLowerCase();
        const nameField = language === 'fr' ? 'name_fr' : 'name';

        return countries.filter(
          (country) =>
            country[nameField].toLowerCase().includes(term) ||
            country.code.toLowerCase().includes(term)
        );
      })
    );
  }

  /**
   * Récupère les pays triés par nom
   * @param language - Langue pour le tri ('en' ou 'fr')
   * @returns Observable<Country[]>
   */
  getCountriesSorted(language: 'en' | 'fr' = 'fr'): Observable<Country[]> {
    return this.getCountries().pipe(
      map((countries) => {
        const nameField = language === 'fr' ? 'name_fr' : 'name';
        return countries.sort((a, b) =>
          a[nameField].localeCompare(b[nameField])
        );
      })
    );
  }

  /**
   * Récupère les pays d'une région spécifique
   * @param region - Région (ex: 'Europe', 'Africa', etc.)
   * @returns Observable<Country[]>
   */
  getCountriesByRegion(region: string): Observable<Country[]> {
    // Cette méthode peut être étendue pour filtrer par région
    // Pour l'instant, retourne tous les pays
    return this.getCountries();
  }

  /**
   * Vide le cache des pays
   */
  clearCache(): void {
    this.countries = [];
    this.countriesLoaded = false;
  }
}
